import React from "react";
import { CONSTANTS } from "../../constants/Constants";
import { UsersTableProps } from "../../types/users";
import { getInitials } from "../../utils/helpers";

const UsersTable: React.FC<UsersTableProps> = ({
    tableHeader,
    tableRows,
    onEdit,
    onDelete,
    currentUser,
}) => {

    return (
        <table className="w-full border-collapse border border-gray-300">
            <thead>
                <tr className="bg-gray-100">
                    {tableHeader.map((header, index) => (
                        <th key={header.key + index} className="py-2 px-3 text-left text-gray-500">
                            {header.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {tableRows.length > 0 ? (
                    tableRows.map((row, index) => {
                        const isCurrentUser = currentUser?.user?.email && currentUser.user?.email === row.email;
                        return (
                            <tr
                                key={row.id || `row-${index}`}
                                className="border-b border-gray-300 hover:bg-gray-50 transition-colors"
                            >
                                {/* User Column */}
                                <td className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                                            {row.image ? (
                                                <img
                                                    src={row.image}
                                                    alt={row.name}
                                                    className="w-10 h-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <span>{getInitials(row.name.split(" ")[0], row.name.split(" ")[1])}</span>
                                            )}
                                        </div>
                                        <div>
                                            <div className="text-gray-900">{row.name}</div>
                                            <div className="text-sm text-gray-500">{row.email}</div>
                                        </div>
                                    </div>
                                </td>

                                {/* Role Column */}
                                <td className="p-4">
                                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium whitespace-nowrap">
                                        {row.roleName}
                                    </span>
                                </td>

                                {/* Status Column */}
                                <td className="py-2 px-3">
                                    <span
                                        className={`px-3 py-1 text-sm rounded font-medium ${row.status?.toLowerCase() === "enabled"
                                            ? "bg-green-200 text-green-800"
                                            : "bg-gray-200 text-gray-800"
                                            }`}
                                    >
                                        {row.status}
                                    </span>
                                </td>

                                {/* Actions Column */}
                                <td className="p-4">
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEdit?.(row.id);
                                            }}
                                            disabled={isCurrentUser}
                                            className={`p-2 rounded transition-colors ${isCurrentUser
                                                ? 'opacity-50 cursor-not-allowed bg-gray-100'
                                                : 'hover:bg-gray-100'
                                                }`}
                                            title={isCurrentUser ? 'Cannot edit your own account' : CONSTANTS.EDIT_TXT}
                                        >
                                            <svg className={`w-5 h-5 ${isCurrentUser ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete?.(row.id);
                                            }}
                                            disabled={isCurrentUser}
                                            className={`p-2 rounded transition-colors ${isCurrentUser
                                                ? 'opacity-50 cursor-not-allowed bg-gray-100'
                                                : 'hover:bg-gray-100'
                                                }`}
                                            title={isCurrentUser ? 'Cannot delete your own account' : CONSTANTS.DELETE_TXT}
                                        >
                                            <svg className={`w-5 h-5 ${isCurrentUser ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })
                ) : (
                    <tr>
                        <td colSpan={tableHeader.length} className="p-5 text-center">
                            {CONSTANTS.NO_DATA_AVAILABLE}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};

export default UsersTable;
